/**
 * The room's status, fetched when the room changes, kept by the room's stream
 * after that, and written back on save.
 *
 * The stream sends it on connect and whenever it changes, which includes a
 * section being cleared: that only stamps the old revision, so there is no
 * message to notice it by. A status the stream sent is at least as new as an
 * answer still in flight, so that answer is dropped.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Channel, RoomStatus } from '../api/types'
import type { OnRoomFrame } from '../live/useLiveMessages'

export function useRoomStatus(channel: Channel | null, onRoomFrame: OnRoomFrame) {
  const [status, setStatus] = useState<RoomStatus | null>(null)
  const channelId = channel?.id ?? null

  useEffect(() => {
    if (!channelId) {
      setStatus(null)
      return
    }
    let current = true
    const stop = onRoomFrame((frame) => {
      if (frame.name !== 'status') return
      current = false
      setStatus(frame.data)
    })
    api
      .status(channelId)
      // A status that will not load leaves the button reading "never set",
      // which is wrong but harmless, and is better than an error over a room
      // somebody is trying to read.
      .then((loaded) => current && setStatus(loaded))
      .catch(() => current && setStatus(null))
    return () => {
      current = false
      stop()
    }
  }, [channelId, onRoomFrame])

  const saveStatus = useCallback(
    async (sections: { now?: string; blocked?: string; up_next?: string }) => {
      if (!channelId) return
      setStatus(await api.setStatus(channelId, sections))
    },
    [channelId],
  )

  return { status, saveStatus }
}
